import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { auth } from '@atlaskit/outbound-auth-flow-client';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import DefaultAppIcon from '@atlaskit/icon/core/app';
import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import { ModalHeader } from '@atlaskit/modal-dialog';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { FallbackableImage } from '@confluence/fallbackable-image';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useLoomCrossJoinUrl } from '@confluence/loom-utils/entry-points/useLoomCrossJoinUrl.ts';

import connectArrowSvg from './img/connect-arrow.svg';
import {
	EXTERNAL_PRODUCT_CONNECTION,
	EXTERNAL_PRODUCT_CONNECTION_ICON_URLS,
	PRODUCT_ICON_MAP,
	type AuthenticationStatus,
	type ExternalProductConnection,
} from './__types__/RemoteObject.ts';

const i18n = defineMessages({
	title: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.title',
		defaultMessage: 'Connect apps for more suggestions',
		description: 'Title on the left panel of the third-party authentication modal',
	},
	description: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.description',
		defaultMessage: 'Connect at least one app to see curated suggestions here.',
		description: 'Description on the left panel of the third-party authentication modal',
	},
	loom: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.option.loom',
		defaultMessage: 'Loom',
		description: 'Label for the Loom auth option in the third-party authentication modal',
	},
	loomDescription: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.option.loom.description',
		defaultMessage: 'Surface videos, recordings and transcripts',
		description: 'Description for the Loom auth option in the third-party authentication modal',
	},
	microsoftTeams: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.option.microsoft-teams',
		defaultMessage: 'Microsoft Office',
		description:
			'Label for the Microsoft Office auth option in the third-party authentication modal',
	},
	microsoftTeamsDescription: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.option.microsoft-teams.description',
		defaultMessage: 'Bring in documents, presentations and files',
		description:
			'Description for the Microsoft Office auth option in the third-party authentication modal',
	},
	googleDrive: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.option.google-drive',
		defaultMessage: 'Google Drive',
		description: 'Label for the Google Drive auth option in the third-party authentication modal',
	},
	googleDriveDescription: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.option.google-drive.description',
		defaultMessage: 'Surface docs, sheets, slides and folders',
		description:
			'Description for the Google Drive auth option in the third-party authentication modal',
	},
	connect: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.connect',
		defaultMessage: 'Connect',
		description: 'Button label to connect a third-party app in the authentication modal',
	},
	untitled: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.preview.untitled',
		defaultMessage: 'Untitled',
		description:
			'Fallback space name in the connect-apps modal illustration when the name is unknown',
	},
	spaceAvatarAlt: {
		id: 'side-navigation.space-navigation.third-party-auth.modal.preview.space-avatar-alt',
		defaultMessage: 'Avatar for {spaceName}',
		description: 'Alternative text for the space avatar in the connect-apps modal illustration',
	},
});

const OPTION_CONFIG: Record<
	ExternalProductConnection,
	{
		icon: string;
		labelMessage: (typeof i18n)[keyof typeof i18n];
		descriptionMessage: (typeof i18n)[keyof typeof i18n];
	}
> = {
	[EXTERNAL_PRODUCT_CONNECTION.LOOM]: {
		icon: EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.LOOM],
		labelMessage: i18n.loom,
		descriptionMessage: i18n.loomDescription,
	},
	[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS]: {
		icon: EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS],
		labelMessage: i18n.microsoftTeams,
		descriptionMessage: i18n.microsoftTeamsDescription,
	},
	[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE]: {
		icon: EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE],
		labelMessage: i18n.googleDrive,
		descriptionMessage: i18n.googleDriveDescription,
	},
};

const ILLUSTRATION_FLOATING_ICONS: string[] = [
	EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS],
	EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE],
	EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.LOOM],
];

const ILLUSTRATION_OVERLAY_ROWS = PRODUCT_ICON_MAP.slice(0, 6);

const styles = cssMap({
	layout: {
		height: '90vh',
		borderRadius: token('radius.xlarge'),
	},
	leftPanel: {
		flexGrow: 1,
		flexBasis: '50%',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		overflowY: 'auto',
		paddingBlock: token('space.500'),
		paddingInline: token('space.400'),
	},
	optionRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.200'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.xlarge'),
	},
	optionIcon: {
		width: '24px',
		height: '24px',
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	optionLabel: {
		flexGrow: 1,
	},
	rightPanel: {
		flexGrow: 1,
		flexBasis: '50%',
		overflow: 'hidden',
		position: 'relative',
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#8fb8f6',
		borderStartEndRadius: token('radius.xlarge'),
		borderEndEndRadius: token('radius.xlarge'),
	},
	closeButtonWrapper: {
		top: token('space.100'),
		left: token('space.100'),
	},
	container: {
		position: 'absolute',
		top: token('space.1000'),
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		left: `calc(${token('space.1000')} + ${token('space.600')})`,
		right: token('space.0'),
		bottom: token('space.0'),
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.xlarge'),
		overflow: 'visible',
	},
	spaceHeader: {
		paddingBlock: token('space.250'),
		paddingInline: token('space.300'),
		backgroundColor: token('color.background.input'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		borderStartStartRadius: token('radius.xlarge'),
		alignItems: 'center',
		gap: token('space.100'),
		flexShrink: 0,
	},
	spaceAvatarWrapper: {
		width: '24px',
		height: '24px',
		borderRadius: token('radius.medium'),
		overflow: 'hidden',
		flexShrink: 0,
	},
	spaceHeaderTitle: {
		minWidth: '0%',
		flexGrow: 1,
	},
	previewPanelContent: {
		paddingBlockEnd: token('space.200'),
	},
	previewCard: {
		backgroundColor: token('color.background.input'),
		padding: token('space.150'),
	},
	previewFoldersList: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	previewRow2Stack: {
		position: 'relative',
		width: '100%',
		minWidth: '0%',
	},
	previewIllustrationTrackOverlay: {
		position: 'absolute',
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		insetInlineEnd: `calc(100% - ${token('space.200')})`,
		marginInlineEnd: token('space.100'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		top: '50%',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		transform: 'translateY(-50%)',
		zIndex: 100,
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.025'),
		pointerEvents: 'none',
	},
	previewListAlignedWithPlaceholders: {
		width: '100%',
		paddingInline: token('space.050'),
		position: 'relative',
		zIndex: 1,
	},
	previewPlaceholderSection: {
		paddingInline: token('space.050'),
		display: 'flex',
		flexDirection: 'column',
		paddingBlock: token('space.100'),
	},
	previewPlaceholderRow: {
		display: 'flex',
		alignItems: 'center',
		paddingBlock: token('space.100'),
	},
	previewPlaceholderDot: {
		width: '10px',
		height: '10px',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral'),
		flexShrink: 0,
	},
	previewPlaceholderBarLong: {
		height: '8px',
		width: '70%',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral'),
	},
	previewPlaceholderBarFull: {
		height: '8px',
		width: '88%',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral'),
	},
	previewGroupsSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		width: '100%',
		borderWidth: token('border.width.focused'),
		borderStyle: 'solid',
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderColor: '#8fb8f6',
		borderRadius: token('radius.medium'),
		padding: token('space.050'),
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: 'rgb(143, 184, 246) 6px 6px 0px 0px',
	},
	illustrationTrack: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.025'),
		flexShrink: 0,
	},
	connectArrowWrap: {
		flexShrink: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		width: '64px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
	},
	connectArrowIllustration: {
		display: 'block',
		width: '100%',
		height: 'auto',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		maxHeight: '56px',
		objectFit: 'contain',
		objectPosition: 'left center',
	},
	floatingIconsColumn: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		flexShrink: 0,
		gap: token('space.100'),
	},
	floatingIconStagger0: {
		marginInlineStart: token('space.150'),
	},
	floatingIconStagger1: {
		marginInlineStart: token('space.negative.075'),
	},
	floatingIconStagger2: {
		marginInlineStart: token('space.150'),
	},
	previewIllustrationFolderHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
		paddingInline: token('space.150'),
		paddingBlock: token('space.075'),
		borderRadius: token('radius.small'),
		position: 'relative',
	},
	previewIllustrationFolderTitleRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		minWidth: '0%',
		flexGrow: 1,
	},
	previewIllustrationFolderTitleBar: {
		height: '8px',
		flexGrow: 1,
		minWidth: '0%',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral'),
	},
	illustrationOverlayRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		minWidth: '0%',
		width: '100%',
		paddingInlineStart: token('space.400'),
		paddingInlineEnd: token('space.075'),
		paddingBlock: token('space.050'),
	},
	illustrationIconSlot: {
		width: '16px',
		height: '16px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
	},
	illustrationSkeletonBar: {
		height: '8px',
		flexGrow: 1,
		minWidth: '0%',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral'),
	},
	floatingIconTile: {
		width: '40px',
		height: '40px',
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		boxShadow: token('elevation.shadow.raised'),
	},
});

const FLOATING_ICON_STAGGER = [
	styles.floatingIconStagger0,
	styles.floatingIconStagger1,
	styles.floatingIconStagger2,
] as const;

type AuthOptionRowProps = {
	option: ExternalProductConnection;
	onConnect?: (option: ExternalProductConnection) => void;
};

const AuthOptionRow = ({ option, onConnect }: AuthOptionRowProps) => {
	const intl = useIntl();
	const { icon, labelMessage, descriptionMessage } = OPTION_CONFIG[option];
	const label = intl.formatMessage(labelMessage);
	const description = intl.formatMessage(descriptionMessage);

	return (
		<Box xcss={styles.optionRow} role="listitem">
			<Box xcss={styles.optionIcon}>
				<img src={icon} alt="" width={24} height={24} />
			</Box>
			<Box xcss={styles.optionLabel}>
				<Stack space="space.050">
					<Text weight="medium">{label}</Text>
					<Text size="small" color="color.text.subtle">
						{description}
					</Text>
				</Stack>
			</Box>
			<Button appearance="default" onClick={() => onConnect?.(option)}>
				{intl.formatMessage(i18n.connect)}
			</Button>
		</Box>
	);
};

type ModalLeftPanelProps = {
	unauthenticatedServices: Partial<Record<ExternalProductConnection, AuthenticationStatus>>;
};

const ModalLeftPanel = ({ unauthenticatedServices }: ModalLeftPanelProps) => {
	const intl = useIntl();
	const loomCrossJoinUrl = useLoomCrossJoinUrl();

	const handleConnect = useCallback(
		async (option: ExternalProductConnection) => {
			switch (option) {
				case EXTERNAL_PRODUCT_CONNECTION.LOOM:
					window.open(loomCrossJoinUrl, '_blank', 'noreferrer');
					break;
				case EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS:
				case EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE: {
					const authUrl = unauthenticatedServices[option]?.outboundAuthUrl;
					if (authUrl) {
						await auth(authUrl);
					}
					break;
				}
				default:
					break;
			}
		},
		[loomCrossJoinUrl, unauthenticatedServices],
	);

	return (
		<Box xcss={styles.leftPanel} testId="third-party-auth-modal-left-panel">
			<Stack space="space.200">
				<Heading size="medium">{intl.formatMessage(i18n.title)}</Heading>
				<Text color="color.text.subtle">{intl.formatMessage(i18n.description)}</Text>
			</Stack>
			<Stack space="space.150" role="list">
				{Object.keys(unauthenticatedServices).map((service) => (
					<AuthOptionRow
						key={service}
						option={service as ExternalProductConnection}
						onConnect={handleConnect}
					/>
				))}
			</Stack>
		</Box>
	);
};

type ModalRightPanelProps = {
	spaceName?: string;
	iconPath?: string;
};

const ModalRightPanel = ({ spaceName, iconPath }: ModalRightPanelProps) => {
	const intl = useIntl();
	const resolvedSpaceName = spaceName || intl.formatMessage(i18n.untitled);
	const avatarAlt = intl.formatMessage(i18n.spaceAvatarAlt, { spaceName: resolvedSpaceName });

	return (
		<Box xcss={styles.rightPanel} testId="third-party-auth-modal-right-panel">
			<Box xcss={styles.closeButtonWrapper}>
				<ModalHeader hasCloseButton />
			</Box>
			<Box xcss={styles.container}>
				<Flex xcss={styles.spaceHeader}>
					<Box xcss={styles.spaceAvatarWrapper}>
						<FallbackableImage
							src={iconPath || ''}
							width="24"
							height="24"
							alt={avatarAlt}
							fallback={() => <DefaultAppIcon label={avatarAlt} />}
						/>
					</Box>
					<Box xcss={styles.spaceHeaderTitle}>
						<Text weight="medium">{resolvedSpaceName}</Text>
					</Box>
				</Flex>
				<Box xcss={styles.previewPanelContent}>
					<Box xcss={styles.previewCard}>
						<Stack space="space.100" xcss={styles.previewFoldersList}>
							<Box>
								<Stack
									space="space.100"
									xcss={styles.previewPlaceholderSection}
									testId="third-party-auth-modal-preview-placeholder-top"
								>
									<Flex xcss={styles.previewPlaceholderRow}>
										<Box xcss={styles.previewPlaceholderDot} />
										<Box xcss={styles.previewPlaceholderBarLong} />
									</Flex>
									<Box xcss={styles.previewPlaceholderBarFull} />
								</Stack>
							</Box>
							<Box xcss={styles.previewRow2Stack}>
								<Box xcss={styles.previewIllustrationTrackOverlay} aria-hidden="true">
									<Box xcss={styles.illustrationTrack}>
										<Box xcss={styles.floatingIconsColumn}>
											{ILLUSTRATION_FLOATING_ICONS.map((src, index) => (
												<Box
													key={src}
													xcss={cx(styles.floatingIconTile, FLOATING_ICON_STAGGER[index])}
												>
													<img src={src} alt="" width={24} height={24} />
												</Box>
											))}
										</Box>
										<Box xcss={styles.connectArrowWrap}>
											<img
												src={connectArrowSvg}
												alt=""
												aria-hidden="true"
												css={styles.connectArrowIllustration}
											/>
										</Box>
									</Box>
								</Box>
								<Box xcss={styles.previewListAlignedWithPlaceholders}>
									<Box
										xcss={styles.previewGroupsSection}
										testId="third-party-auth-modal-preview-overlay"
									>
										<Flex xcss={styles.previewIllustrationFolderHeader}>
											<Flex xcss={styles.previewIllustrationFolderTitleRow}>
												<FolderClosedIcon label="" color={token('color.icon.selected')} />
												<Box xcss={styles.previewIllustrationFolderTitleBar} />
											</Flex>
										</Flex>
										<Stack space="space.075">
											{ILLUSTRATION_OVERLAY_ROWS.map(({ iconUrl, productKey }) => (
												<Flex key={productKey} xcss={styles.illustrationOverlayRow}>
													<Box xcss={styles.illustrationIconSlot}>
														<img src={iconUrl} alt="" width={16} height={16} />
													</Box>
													<Box xcss={styles.illustrationSkeletonBar} />
												</Flex>
											))}
										</Stack>
									</Box>
								</Box>
							</Box>
							<Box>
								<Stack
									space="space.100"
									xcss={styles.previewPlaceholderSection}
									testId="third-party-auth-modal-preview-placeholder-bottom"
								>
									<Flex xcss={styles.previewPlaceholderRow}>
										<Box xcss={styles.previewPlaceholderDot} />
										<Box xcss={styles.previewPlaceholderBarLong} />
									</Flex>
									<Box xcss={styles.previewPlaceholderBarFull} />
								</Stack>
							</Box>
						</Stack>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export type ThirdPartyAuthenticationModalProps = {
	unauthenticatedServices: Partial<Record<ExternalProductConnection, AuthenticationStatus>>;
	onClose: () => void;
	spaceName?: string;
	iconPath?: string;
};

export const ThirdPartyAuthenticationModal = ({
	unauthenticatedServices,
	onClose,
	spaceName,
	iconPath,
}: ThirdPartyAuthenticationModalProps) => {
	const { Modal } = useChoreographerAssets();

	return (
		<Modal onClose={onClose} width="90vw">
			<Flex xcss={styles.layout}>
				<ModalLeftPanel unauthenticatedServices={unauthenticatedServices} />
				<ModalRightPanel spaceName={spaceName} iconPath={iconPath} />
			</Flex>
		</Modal>
	);
};
